import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useComplianceAnalysis } from '../../../hooks/useComplianceAnalysis';

const ComplianceAnalysis = ({ 
  analysis, 
  onAcceptSuggestion, 
  onRejectSuggestion,
  content = null 
}) => {
  const [expandedViolations, setExpandedViolations] = useState(new Set());
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  
  const {
    isAnalyzing,
    error,
    explainRule,
    generateSuggestions
  } = useComplianceAnalysis();

  const getSeverityColor = (severity) => {
    const colors = {
      'Critical': 'text-red-600 bg-red-50 border-red-200',
      'High': 'text-orange-600 bg-orange-50 border-orange-200',
      'Medium': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Low': 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      'Critical': 'AlertTriangle',
      'High': 'AlertCircle',
      'Medium': 'Info',
      'Low': 'CheckCircle'
    };
    return icons?.[severity] || 'Info';
  };

  const toggleViolationExpansion = (violationId) => {
    const newExpanded = new Set(expandedViolations);
    if (newExpanded?.has(violationId)) {
      newExpanded?.delete(violationId);
    } else {
      newExpanded?.add(violationId);
    }
    setExpandedViolations(newExpanded);
  };

  const handleAcceptSuggestion = (violationId, suggestionIndex) => {
    if (onAcceptSuggestion) {
      onAcceptSuggestion(violationId, suggestionIndex);
    }
  };

  const handleRejectSuggestion = (violationId, suggestionIndex) => {
    if (onRejectSuggestion) {
      onRejectSuggestion(violationId, suggestionIndex);
    }
  };

  const handleExplainRule = async (regulation, context) => {
    const explanation = await explainRule(regulation, context);
    if (explanation) {
      // Show explanation in a modal or expanded section
      console.log('Rule explanation:', explanation);
    }
  };

  const handleGenerateAISuggestions = async (violation) => {
    if (!content?.text && !content?.preview) return;
    
    const contentText = content?.text || content?.preview;
    const suggestions = await generateSuggestions(contentText, [violation], content?.platform);
    
    if (suggestions) {
      setSelectedSuggestion({ violation, suggestions });
      setShowAIPanel(true);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">AI Compliance Analysis</h2>
          <div className="flex items-center space-x-2">
            <div className={`px-2 py-1 rounded-md text-xs font-medium border ${getSeverityColor(analysis?.overallRisk)}`}>
              {analysis?.overallRisk} Risk
            </div>
            <div className="text-sm text-muted-foreground">
              Score: <span className="font-medium text-foreground">{analysis?.complianceScore}/100</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="text-lg font-semibold text-red-600">{analysis?.violationCounts?.critical}</div>
            <div className="text-xs text-red-600">Critical</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
            <div className="text-lg font-semibold text-orange-600">{analysis?.violationCounts?.high}</div>
            <div className="text-xs text-orange-600">High</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="text-lg font-semibold text-yellow-600">{analysis?.violationCounts?.medium}</div>
            <div className="text-xs text-yellow-600">Medium</div>
          </div>
        </div>
      </div>

      {/* Enhanced AI Controls */}
      <div className="p-4 border-b border-border bg-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Brain" size={16} className="text-purple-600" />
            <span className="text-sm font-medium text-purple-800">AI-Powered Analysis</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            iconName="Sparkles"
            onClick={() => setShowAIPanel(!showAIPanel)}
            className="text-purple-600 border-purple-200"
          >
            Enhance with AI
          </Button>
        </div>
        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1">
        {analysis?.violations?.map((violation, index) => (
          <div key={violation?.id || index} className="border-b border-border last:border-b-0">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(violation?.severity)}`}>
                      {violation?.severity}
                    </span>
                    <h4 className="font-medium text-foreground">{violation?.rule}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {violation?.regulation}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Brain"
                    onClick={() => handleGenerateAISuggestions(violation)}
                    disabled={isAnalyzing}
                    className="text-purple-600 border-purple-200"
                  >
                    AI Fix
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName="HelpCircle"
                    onClick={() => handleExplainRule(violation?.regulation, violation?.flaggedText)}
                  >
                    Explain
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Flagged Content</h4>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800 font-mono">{violation?.flaggedText}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Detailed Explanation</h4>
                  <p className="text-sm text-muted-foreground">{violation?.explanation}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Regulatory Citation</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">{violation?.citation}</p>
                  </div>
                </div>

                {/* Enhanced Suggestions with AI Badge */}
                {violation?.suggestions?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <h5 className="text-sm font-medium text-foreground flex items-center space-x-2">
                      <span>AI Suggestions</span>
                      <Icon name="Sparkles" size={14} className="text-purple-500" />
                    </h5>
                    {violation?.suggestions?.map((suggestion, suggestionIndex) => (
                      <div
                        key={suggestionIndex}
                        className="p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-sm text-foreground font-medium">
                            "{suggestion?.text}"
                          </p>
                          <div className="flex space-x-1 ml-3">
                            <Button
                              size="sm"
                              variant="outline"
                              iconName="Check"
                              onClick={() => onAcceptSuggestion(violation?.id, suggestionIndex)}
                            >
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              iconName="X"
                              onClick={() => onRejectSuggestion(violation?.id, suggestionIndex)}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-green-700">{suggestion?.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {analysis?.violations?.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Violations Found</h3>
            <p className="text-muted-foreground">This content appears to be compliant with regulations.</p>
          </div>
        )}
      </div>

      {/* AI Suggestions Panel */}
      {showAIPanel && selectedSuggestion && (
        <div className="absolute inset-0 bg-card border border-border rounded-lg z-10">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold flex items-center space-x-2">
              <Icon name="Brain" className="text-purple-600" />
              <span>AI-Generated Suggestions</span>
            </h3>
            <Button
              size="sm"
              variant="ghost"
              iconName="X"
              onClick={() => {
                setShowAIPanel(false);
                setSelectedSuggestion(null);
              }}
            />
          </div>
          
          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            {selectedSuggestion?.suggestions?.suggestions?.map((suggestion, index) => (
              <div key={index} className="p-3 border border-border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium">{suggestion?.version}</span>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                    {suggestion?.complianceScore}/100
                  </span>
                </div>
                <p className="text-sm mb-2 p-2 bg-muted rounded">{suggestion?.content}</p>
                <p className="text-xs text-muted-foreground mb-3">{suggestion?.changes}</p>
                <Button
                  size="sm"
                  onClick={() => {
                    onAcceptSuggestion(selectedSuggestion?.violation?.id, index);
                    setShowAIPanel(false);
                    setSelectedSuggestion(null);
                  }}
                  iconName="Check"
                >
                  Apply This Version
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceAnalysis;